import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { GenerationStage } from "@/components/loading";
import { GenerationLoadingState, OnboardingStepSkeleton } from "@/components/loading";
import { OnboardingHeader } from "@/components/patterns/headers";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/hooks/use-session";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import {
      NO_PERSONA_AGE_RANGE_VALUE,
      PERSONA_AGE_RANGES,
      toNullablePersonaAgeRange,
      toPersonaAgeRangeFormValue,
} from "@/lib/persona";
import { hasCompletedBrandOnboarding } from "@/lib/onboarding-progress";
import { resolveOnboardingSearchSpecification } from "@/lib/onboarding-search-specification";
import {
      ONBOARDING_PERSONA_GENERATION_SLOW_WARNING_MS,
      shouldAutoStartPersonaGeneration,
} from "@/lib/onboarding-generation";
import { useTRPC } from "@/lib/trpc";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";

export const Route = createFileRoute("/onboarding/brand/$brandId/persona")({
      component: RouteComponent,
});

const PERSONA_GENERATION_STAGES: GenerationStage[] = [
      { id: "RESEARCHING", label: "Researching", progressRange: [5, 55] },
      { id: "FORMATTING", label: "Structuring", progressRange: [55, 90] },
];

const PERSONA_TIPS = [
      "Your persona helps us tailor AI search queries to match how your customers actually search.",
      "Upsearch monitors how AI search engines like ChatGPT and Perplexity represent your brand.",
      "A well-defined persona means more relevant prompts — and more actionable visibility insights.",
      "You'll be able to track brand visibility, sentiment, and competitor mentions across AI platforms.",
];

type PersonaGenerationStage = "IDLE" | "RESEARCHING" | "FORMATTING";
type PersonaGenerationBroadcastStage = "RESEARCHING" | "FORMATTING" | "COMPLETED" | "FAILED";

const trimSafe = (value: string | null | undefined): string => (value ?? "").trim();
const toNullableTrimmed = (value: string | null | undefined): string | null => {
      const trimmed = trimSafe(value);
      return trimmed.length > 0 ? trimmed : null;
};
const getErrorMessage = (error: unknown): string => {
      if (error instanceof Error && error.message) {
            return error.message;
      }
      if (typeof error === "object" && error !== null && "message" in error) {
            return String((error as { message?: unknown }).message ?? "");
      }
      return "Unknown error";
};

export function RouteComponent() {
      const { brandId } = Route.useParams();
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const navigate = useNavigate();
      const { sessionData, isLoading: isLoadingSession } = useSession();

      const [isSubmitting, setIsSubmitting] = useState(false);

      const setCurrentSearchSpecificationId = useSearchSpecificationStore(
            (state) => state.setCurrentSearchSpecificationId,
      );

      const [personaName, setPersonaName] = useState("");
      const [personaDescription, setPersonaDescription] = useState("");
      const [personaAgeRange, setPersonaAgeRange] = useState("");
      const [personaEducationLevel, setPersonaEducationLevel] = useState("");
      const [personaLocation, setPersonaLocation] = useState("");
      const [personaGenerationStage, setPersonaGenerationStage] = useState<PersonaGenerationStage>("IDLE");
      const [activePersonaGenerationId, setActivePersonaGenerationId] = useState<string | null>(null);
      const [hasAutoTriggeredPersonaGeneration, setHasAutoTriggeredPersonaGeneration] = useState(false);
      const activePersonaGenerationIdRef = useRef<string | null>(null);
      const personaGenerationWarningTimeoutRef = useRef<number | null>(null);
      const hasShownSlowGenerationWarningRef = useRef(false);

      const [onboardingPersonaId, setOnboardingPersonaId] = useState<string | null>(null);

      const { data: brandBeingCreated, isLoading: isLoadingBrand } = useQuery(
            trpc.brands.getBrand.queryOptions({ id: brandId }, { enabled: !!sessionData && !!brandId }),
      );
      const { data: brandSearchExecutionCount, isLoading: isLoadingBrandSearchExecutionCount } = useQuery(
            trpc.brands.getBrandSearchExecutionsCount.queryOptions(
                  { brandId },
                  { enabled: !!sessionData && !!brandId },
            ),
      );

      const { data: existingPersonas, isLoading: isLoadingPersonas } = useQuery(
            trpc.personas.getBrandPersonas.queryOptions({ brandId }, { enabled: !!sessionData && !!brandId }),
      );

      const { data: demoLlmsIds } = useQuery(trpc.llms.getDemoLlmsIds.queryOptions());

      const generatePersonaMutation = useMutation(
            trpc.personas.generatePersona.mutationOptions({
                  onError: (error, variables) => {
                        if (activePersonaGenerationIdRef.current !== variables.generationId) return;
                        toast.error(error.message || "Failed to start persona generation");
                        clearPersonaGeneration();
                  },
            }),
      );
      const createPersonaMutation = useMutation(trpc.personas.createPersona.mutationOptions({}));
      const updatePersonaMutation = useMutation(trpc.personas.updatePersona.mutationOptions({}));
      const createSearchSpecificationMutation = useMutation(
            trpc.searchSpecifications.createSearchSpecification.mutationOptions({}),
      );
      const updateSearchSpecificationMutation = useMutation(
            trpc.searchSpecifications.updateSearchSpecification.mutationOptions({}),
      );

      useEffect(() => {
            if (!sessionData && !isLoadingSession) {
                  navigate({ to: "/auth", replace: true });
            }
      }, [sessionData, isLoadingSession, navigate]);

      useEffect(() => {
            if (!isLoadingBrand && sessionData && !brandBeingCreated) {
                  toast.error("Brand not found or you do not have access.");
                  navigate({ to: "/onboarding/brand/new", replace: true });
            }
      }, [isLoadingBrand, sessionData, brandBeingCreated, navigate]);

      useEffect(() => {
            if (!brandBeingCreated?.slug || isLoadingBrandSearchExecutionCount) return;
            if (!hasCompletedBrandOnboarding(brandSearchExecutionCount)) return;

            navigate({
                  to: "/$brand/dashboard",
                  params: { brand: brandBeingCreated.slug },
                  replace: true,
            });
      }, [
            brandBeingCreated?.slug,
            brandSearchExecutionCount,
            isLoadingBrandSearchExecutionCount,
            navigate,
      ]);

      useEffect(() => {
            if (
                  existingPersonas &&
                  existingPersonas.length > 0 &&
                  !onboardingPersonaId &&
                  !hasAutoTriggeredPersonaGeneration
            ) {
                  const persona = existingPersonas[0];
                  setOnboardingPersonaId(persona.id);
                  setPersonaName(persona.name);
                  setPersonaDescription(persona.description || "");
                  setPersonaAgeRange(toPersonaAgeRangeFormValue(persona.ageRange));
                  setPersonaEducationLevel(persona.educationLevel || "");
                  setPersonaLocation(persona.location || "");
                  setHasAutoTriggeredPersonaGeneration(true);
            }
      }, [existingPersonas, onboardingPersonaId, hasAutoTriggeredPersonaGeneration]);

      const clearPersonaGeneration = useCallback(() => {
            activePersonaGenerationIdRef.current = null;
            setActivePersonaGenerationId(null);
            setPersonaGenerationStage("IDLE");
            hasShownSlowGenerationWarningRef.current = false;
            if (personaGenerationWarningTimeoutRef.current !== null) {
                  window.clearTimeout(personaGenerationWarningTimeoutRef.current);
                  personaGenerationWarningTimeoutRef.current = null;
            }
      }, []);

      const personaBroadcast = useSupabaseBroadcast({
            channelName: `persona_generation:${brandBeingCreated?.id ?? "unknown"}`,
            event: "persona_generation_progress",
            callbackFn: (broadcastMessage: {
                  payload?: {
                        generationId?: string;
                        stage?: PersonaGenerationBroadcastStage;
                        persona?: {
                              name: string;
                              description: string | null;
                              ageRange: string | null;
                              educationLevel: string | null;
                              location: string | null;
                        };
                        error?: string;
                  };
            }) => {
                  const payload = broadcastMessage.payload;
                  if (!payload?.generationId || payload.generationId !== activePersonaGenerationIdRef.current) return;

                  if (payload.stage === "RESEARCHING" || payload.stage === "FORMATTING") {
                        setPersonaGenerationStage(payload.stage);
                        return;
                  }

                  if (payload.stage === "COMPLETED" && payload.persona) {
                        setPersonaName(payload.persona.name ?? "");
                        setPersonaDescription(payload.persona.description ?? "");
                        setPersonaAgeRange(toPersonaAgeRangeFormValue(payload.persona.ageRange));
                        setPersonaEducationLevel(payload.persona.educationLevel ?? "");
                        setPersonaLocation(payload.persona.location ?? "");
                        clearPersonaGeneration();
                        toast.success("Persona generated");
                        return;
                  }

                  if (payload.stage === "FAILED") {
                        clearPersonaGeneration();
                        toast.error(payload.error ?? "Failed to generate persona");
                  }
            },
      });

      const triggerPersonaGeneration = useCallback((): boolean => {
            if (!brandBeingCreated?.id || activePersonaGenerationId) return false;
            if (!personaBroadcast.isSubscribed) {
                  if (personaBroadcast.hasSubscriptionError) {
                        toast.error("Realtime connection is unavailable. Refresh and try again.");
                  }
                  return false;
            }

            const generationId = crypto.randomUUID();
            activePersonaGenerationIdRef.current = generationId;
            setActivePersonaGenerationId(generationId);
            setPersonaGenerationStage("RESEARCHING");
            hasShownSlowGenerationWarningRef.current = false;
            personaGenerationWarningTimeoutRef.current = window.setTimeout(() => {
                  if (activePersonaGenerationIdRef.current !== generationId) return;
                  personaGenerationWarningTimeoutRef.current = null;
                  if (hasShownSlowGenerationWarningRef.current) return;
                  hasShownSlowGenerationWarningRef.current = true;
                  toast("Persona generation is taking longer than usual. We'll keep working on it.");
            }, ONBOARDING_PERSONA_GENERATION_SLOW_WARNING_MS);
            generatePersonaMutation.mutate({ brandId: brandBeingCreated.id, generationId });
            return true;
      }, [
            activePersonaGenerationId,
            brandBeingCreated?.id,
            generatePersonaMutation,
            personaBroadcast.hasSubscriptionError,
            personaBroadcast.isSubscribed,
      ]);

      useEffect(() => {
            if (
                  !shouldAutoStartPersonaGeneration({
                        brandId: brandBeingCreated?.id,
                        hasAutoTriggeredPersonaGeneration,
                        onboardingPersonaId,
                        existingPersonaCount: existingPersonas?.length ?? null,
                        isLoadingPersonas,
                        isBroadcastSubscribed: personaBroadcast.isSubscribed,
                        isGenerating: activePersonaGenerationId !== null,
                  })
            ) {
                  return;
            }

            const started = triggerPersonaGeneration();
            if (started) {
                  setHasAutoTriggeredPersonaGeneration(true);
            }
      }, [
            activePersonaGenerationId,
            brandBeingCreated?.id,
            existingPersonas,
            hasAutoTriggeredPersonaGeneration,
            isLoadingPersonas,
            onboardingPersonaId,
            personaBroadcast.isSubscribed,
            triggerPersonaGeneration,
      ]);

      useEffect(
            () => () => {
                  if (personaGenerationWarningTimeoutRef.current !== null) {
                        window.clearTimeout(personaGenerationWarningTimeoutRef.current);
                  }
            },
            [],
      );

      const resolveOnboardingSearchSpecificationId = async (personaId: string): Promise<string> => {
            if (!brandBeingCreated?.id) {
                  throw new Error("BRAND_NOT_FOUND");
            }

            const resolvedSearchSpecificationId = await resolveOnboardingSearchSpecification({
                  brandId: brandBeingCreated.id,
                  personaId,
                  demoLlmIds: demoLlmsIds ?? [],
                  fetchBrandSearchSpecifications: async ({ brandId: currentBrandId, skip, take }) =>
                        await queryClient.fetchQuery(
                              trpc.searchSpecifications.getBrandSearchSpecifications.queryOptions({
                                    brandId: currentBrandId,
                                    skip,
                                    take,
                              }),
                        ),
                  createSearchSpecification: async (input) =>
                        await createSearchSpecificationMutation.mutateAsync(input),
                  updateSearchSpecification: async (input) =>
                        await updateSearchSpecificationMutation.mutateAsync(input),
            });

            setCurrentSearchSpecificationId(resolvedSearchSpecificationId);
            return resolvedSearchSpecificationId;
      };

      const handleContinueToPromptReview = async () => {
            if (!brandBeingCreated?.id) return;
            if (!trimSafe(personaName)) {
                  toast.error("Persona name is required");
                  return;
            }

            setIsSubmitting(true);
            try {
                  const personaPayload = {
                        name: trimSafe(personaName),
                        description: toNullableTrimmed(personaDescription),
                        ageRange: toNullablePersonaAgeRange(personaAgeRange),
                        educationLevel: toNullableTrimmed(personaEducationLevel),
                        location: toNullableTrimmed(personaLocation),
                  };

                  let personaId = onboardingPersonaId;
                  if (personaId) {
                        await updatePersonaMutation.mutateAsync({
                              id: personaId,
                              source: "onboarding",
                              ...personaPayload,
                        });
                  } else {
                        const createdPersona = await createPersonaMutation.mutateAsync({
                              brandId: brandBeingCreated.id,
                              ...personaPayload,
                        });
                        personaId = createdPersona.id;
                        setOnboardingPersonaId(createdPersona.id);
                  }

                  const resolvedSearchSpecificationId = await resolveOnboardingSearchSpecificationId(personaId);

                  navigate({
                        to: "/onboarding/brand/$brandId/prompts",
                        params: { brandId: brandBeingCreated.id },
                        search: { searchSpecificationId: resolvedSearchSpecificationId },
                  });
            } catch (error) {
                  console.error("Failed onboarding persona step:", error);
                  const message = getErrorMessage(error);
                  toast.error(`Failed to prepare onboarding prompts: ${message}`);
            } finally {
                  setIsSubmitting(false);
            }
      };

      const handleBack = () => {
            navigate({
                  to: "/onboarding/brand/$brandId/profile",
                  params: { brandId },
            });
      };

      if (!sessionData) return null;
      if (isLoadingBrand || isLoadingBrandSearchExecutionCount) {
            return <OnboardingStepSkeleton fieldCount={5} showBackButton />;
      }
      if (!brandBeingCreated) return null;

      const isPersonaGenerating = activePersonaGenerationId !== null;
      const isPersonaStepBusy =
            isSubmitting ||
            createPersonaMutation.isPending ||
            updatePersonaMutation.isPending ||
            createSearchSpecificationMutation.isPending ||
            updateSearchSpecificationMutation.isPending;

      const generateButtonText =
            personaGenerationStage === "FORMATTING"
                  ? "Formatting Persona..."
                  : personaGenerationStage === "RESEARCHING"
                    ? "Generating Persona..."
                    : "Regenerate Persona";

      if (isPersonaGenerating) {
            return (
                  <GenerationLoadingState
                        title="Generating Persona"
                        estimatedDuration="Usually about a minute, but sometimes longer"
                        currentStageId={personaGenerationStage === "IDLE" ? null : personaGenerationStage}
                        stages={PERSONA_GENERATION_STAGES}
                        tips={PERSONA_TIPS}
                  />
            );
      }

      return (
            <div className="w-full">
                  <OnboardingHeader
                        title="Review Generated Persona"
                        description="Confirm or edit the generated persona before prompt generation."
                        className="mb-6"
                  />

                  <div className="flex flex-col gap-5">
                        <div className="flex flex-col gap-5">
                              <div className="relative flex flex-col gap-1.5">
                                    <Label htmlFor="persona-name" variant="brand">
                                          Name *
                                    </Label>
                                    <Input
                                          id="persona-name"
                                          value={personaName}
                                          onChange={(event) => setPersonaName(event.target.value)}
                                          placeholder="e.g. Enterprise Buyer"
                                          variant="brand"
                                          disabled={isPersonaGenerating || isPersonaStepBusy}
                                    />
                              </div>

                              <div className="relative flex flex-col gap-1.5">
                                    <Label htmlFor="persona-description" variant="brand">
                                          Description
                                    </Label>
                                    <Textarea
                                          id="persona-description"
                                          value={personaDescription}
                                          onChange={(event) => setPersonaDescription(event.target.value)}
                                          placeholder="Describe goals, pain points, and search behavior..."
                                          rows={4}
                                          variant="brand"
                                          disabled={isPersonaGenerating || isPersonaStepBusy}
                                    />
                              </div>

                              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                    <div className="relative flex flex-col gap-1.5">
                                          <Label htmlFor="persona-age-range" variant="brand">
                                                Age Range
                                          </Label>
                                          <Select
                                                value={personaAgeRange || NO_PERSONA_AGE_RANGE_VALUE}
                                                onValueChange={(value) =>
                                                      setPersonaAgeRange(
                                                            value === NO_PERSONA_AGE_RANGE_VALUE ? "" : value,
                                                      )
                                                }
                                                disabled={isPersonaGenerating || isPersonaStepBusy}
                                          >
                                                <SelectTrigger id="persona-age-range" variant="brand">
                                                      <SelectValue placeholder="Select age range" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                      <SelectItem value={NO_PERSONA_AGE_RANGE_VALUE}>
                                                            Not specified
                                                      </SelectItem>
                                                      {PERSONA_AGE_RANGES.map((range) => (
                                                            <SelectItem key={range} value={range}>
                                                                  {range}
                                                            </SelectItem>
                                                      ))}
                                                </SelectContent>
                                          </Select>
                                    </div>

                                    <div className="relative flex flex-col gap-1.5">
                                          <Label htmlFor="persona-education-level" variant="brand">
                                                Education
                                          </Label>
                                          <Input
                                                id="persona-education-level"
                                                value={personaEducationLevel}
                                                onChange={(event) => setPersonaEducationLevel(event.target.value)}
                                                placeholder="e.g. Bachelor's"
                                                variant="brand"
                                                disabled={isPersonaGenerating || isPersonaStepBusy}
                                          />
                                    </div>
                              </div>

                              <div className="relative flex flex-col gap-1.5">
                                    <Label htmlFor="persona-location" variant="brand">
                                          Location
                                    </Label>
                                    <Input
                                          id="persona-location"
                                          value={personaLocation}
                                          onChange={(event) => setPersonaLocation(event.target.value)}
                                          placeholder="e.g. North America"
                                          variant="brand"
                                          disabled={isPersonaGenerating || isPersonaStepBusy}
                                    />
                              </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-brand-ink/[0.06] pt-6 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                          type="button"
                                          variant="brandOutline"
                                          onClick={handleBack}
                                          disabled={isPersonaGenerating || isPersonaStepBusy}
                                    >
                                          Back
                                    </Button>

                                    <Button
                                          type="button"
                                          variant="brandAccentOutline"
                                          className="gap-2"
                                          onClick={triggerPersonaGeneration}
                                          disabled={isPersonaGenerating || isPersonaStepBusy}
                                    >
                                          <Sparkles className="h-4 w-4 text-brand-teal" />
                                          {generateButtonText}
                                    </Button>
                              </div>

                              <LoadingButton
                                    variant="brand"
                                    onClick={handleContinueToPromptReview}
                                    loading={isPersonaStepBusy}
                                    loadingText="Preparing Prompts..."
                                    disabled={isPersonaGenerating || isPersonaStepBusy || !trimSafe(personaName)}
                                    className="w-full px-8 sm:w-auto"
                              >
                                    Continue
                              </LoadingButton>
                        </div>
                  </div>
            </div>
      );
}
